import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogContent,
    Divider,
    Grid,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import useGlobalStore from '../../../store/useGlobalStore';
import { toast } from 'sonner';
import CommonDialog from '../../../common/CommonDialog';
import { showTaxSettingsNotice } from '../../../common/TaxSettingsNotice';
import {
    coreSettingsKeys,
    useGetCustomerNotificationTemplates,
    useGetShopConfig,
    useUpdateCustomerNotificationTemplates,
    useUpdateShopConfig,
} from '../../../services/core/settings/settings.hooks';
import MessageTemplateEditor from './MessageTemplateEditor';
import { getGSM7Length, getMissingRequiredVariables, getMissingVariablesErrorMessage, hasInvalidGSM7Characters } from '../../../utility/gsm7Validator';

const StyledSmallToggleButtonGroup = styled(ToggleButtonGroup)(() => ({
    borderRadius: '100px',
    padding: '4px',
    background: '#FFF',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    height: '40px',
    boxSizing: 'border-box',
    border: 'none',
    boxShadow: 'none',

    '& .MuiToggleButtonGroup-grouped': {
        margin: '0 !important',
        border: 'none !important',
        '&:not(:first-of-type)': {
            borderRadius: '50px',
            borderLeft: 'none',
        },
        '&:first-of-type': {
            borderRadius: '50px',
        },
    },

    '& .MuiToggleButton-root': {
        border: 'none',
        borderRadius: '50px !important',
        padding: '6px 14px',
        fontFamily: 'Roboto',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '130%',
        letterSpacing: '-0.48px',
        fontFeatureSettings: "'liga' off, 'clig' off",
        textTransform: 'capitalize',
        height: '32px',
        boxSizing: 'border-box',

        '&.Mui-selected': {
            backgroundColor: '#007AFF',
            color: 'rgba(255, 255, 255, 0.87)',
            border: '1px solid #0066E1 !important',
            boxShadow: '0px 2px 2px 0px rgba(0, 122, 255, 0.25)',
        },

        '&:not(.Mui-selected)': {
            backgroundColor: '#FFF',
            color: 'rgba(0, 0, 0, 0.87)',
            border: 'none !important',
        },

        '&:not(.Mui-selected):hover': {
            backgroundColor: '#FFF',
        },

        '&.Mui-selected:hover': {
            backgroundColor: '#007AFF',
        },
    },
}));

// Exact Double Arrow SVGs provided by user
const DoubleUpArrowIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M17 11L12 6L7 11" stroke="#006CDD" strokeWidth="1.5" strokeLinecap="square" />
        <path d="M17 18L12 13L7 18" stroke="#006CDD" strokeWidth="1.5" strokeLinecap="square" />
    </svg>
);

const DoubleDownArrowIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M17.9981 12.5L11.9981 18.5L5.99805 12.5" stroke="#006CDD" strokeWidth="1.5" />
        <path d="M17.9981 5.49902L11.9981 11.499L5.99805 5.49907" stroke="#006CDD" strokeWidth="1.5" />
    </svg>
);

const ResetAllArrowIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" {...props}>
        <path
            d="M12 4V1L8 5L12 9V6C15.31 6 18 8.69 18 12C18 15.31 15.31 18 12 18C8.69 18 6 15.31 6 12H4C4 16.42 7.58 20 12 20C16.42 20 20 16.42 20 12C20 7.58 16.42 4 12 4Z"
            fill="currentColor"
        />
    </svg>
);

// SVG Icons for each template item
const AppointmentReminder24hIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" {...props}>
        <path d="M13.333 1.66699V5.00033M5.83301 1.66699V5.00033" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
        <path d="M17.083 6.66634V3.33301H2.08301V18.333H6.24967" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
        <path d="M2.08301 8.33301H6.24967" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
        <path
            d="M12.917 18.333C15.6784 18.333 17.917 16.0944 17.917 13.333C17.917 10.5716 15.6784 8.33301 12.917 8.33301C10.1556 8.33301 7.91699 10.5716 7.91699 13.333C7.91699 16.0944 10.1556 18.333 12.917 18.333Z"
            stroke="#006CDD"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M12.917 10.833V13.333L14.5837 14.1663" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
    </svg>
);

const AppointmentReminder2hIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" {...props}>
        <path d="M13.333 1.66699V5.00033M5.83301 1.66699V5.00033" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
        <path d="M17.083 6.66634V3.33301H2.08301V18.333H6.24967" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
        <path d="M2.08301 8.33301H6.24967" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
        <path
            d="M12.917 18.333C15.6784 18.333 17.917 16.0944 17.917 13.333C17.917 10.5716 15.6784 8.33301 12.917 8.33301C10.1556 8.33301 7.91699 10.5716 7.91699 13.333C7.91699 16.0944 10.1556 18.333 12.917 18.333Z"
            stroke="#006CDD"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M12.917 10.833V13.333L14.5837 14.1663" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
    </svg>
);

const AppointmentConfirmedIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" {...props}>
        <path d="M7.5 13.3337L9.16663 15.0003L12.5 11.667" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
        <path d="M13.75 1.66699V5.00033M6.25 1.66699V5.00033" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M2.5 18.3332V3.33301L17.5 3.33317V18.3332H2.5Z"
            stroke="#006CDD"
            strokeWidth="1.25"
            strokeLinecap="square"
        />
        <path d="M2.5 8.33301H17.5" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
    </svg>
);

const AppointmentRescheduledIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" {...props}>
        <path
            d="M17.8877 12.9163C17.3114 11.92 16.2342 11.2497 15.0003 11.2497C13.7275 11.2497 12.6212 11.9631 12.0597 13.0119M12.1129 16.2497C12.6892 17.246 13.7665 17.9163 15.0003 17.9163C16.2692 17.9163 17.3726 17.2073 17.9357 16.1638M14.167 13.333H11.667V10.833M15.8337 15.833H18.3337V18.333"
            stroke="#006CDD"
            strokeWidth="1.25"
            strokeLinecap="square"
        />
        <path d="M13.75 1.66699V5.00033M6.25 1.66699V5.00033" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
        <path d="M10 18.333H2.5V3.33301H17.5V9.16634" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
        <path d="M2.5 8.33301H17.5" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
    </svg>
);

const AfterHoursBookingIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" {...props}>
        <path
            d="M18.25 8.33366C17.4778 4.52993 14.1149 1.66699 10.0833 1.66699C5.48096 1.66699 1.75 5.39795 1.75 10.0003C1.75 14.6027 5.48096 18.3337 10.0833 18.3337C10.9543 18.3337 11.7942 18.2 12.5833 17.9522"
            stroke="#006CDD"
            strokeWidth="1.25"
            strokeLinecap="square"
        />
        <path d="M10.083 6.66699V10.0003L11.7497 11.667" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
        <path d="M16.75 15.0003V11.667" stroke="#006CDD" strokeWidth="1.25" strokeLinecap="square" />
        <path
            d="M16.8535 18.1253H16.7493M16.9577 18.1253C16.9577 18.2404 16.8644 18.3337 16.7493 18.3337C16.6343 18.3337 16.541 18.2404 16.541 18.1253C16.541 18.0102 16.6343 17.917 16.7493 17.917C16.8644 17.917 16.9577 18.0102 16.9577 18.1253Z"
            stroke="#006CDD"
            strokeWidth="1.25"
            strokeLinecap="round"
        />
    </svg>
);

const VehicleReadyIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" {...props}>
        <path
            d="M12.4997 12.9168C15.4913 12.9168 17.9163 10.4917 17.9163 7.50016C17.9163 4.50862 15.4913 2.0835 12.4997 2.0835C9.50809 2.0835 7.08301 4.50862 7.08301 7.50016C7.08301 8.23982 7.23126 8.94483 7.49967 9.58716L7.08301 10.0002L2.08301 15.0002V17.9168H4.99967V16.2502H6.66634V14.5835H8.33301L10.4163 12.5017C11.0577 12.7692 11.7614 12.9168 12.4997 12.9168Z"
            stroke="#006CDD"
            strokeWidth="1.25"
            strokeLinecap="round"
        />
        <path
            d="M15 6.25C15 6.94036 14.4403 7.5 13.75 7.5C13.0597 7.5 12.5 6.94036 12.5 6.25C12.5 5.55964 13.0597 5 13.75 5C14.4403 5 15 5.55964 15 6.25Z"
            stroke="#006CDD"
            strokeWidth="1.25"
        />
    </svg>
);

const GoogleIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" {...props}>
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
        />
        <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
        />
    </svg>
);

const PlusIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" {...props}>
        <path d="M7 2.9165V11.0832" stroke="#006CDD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2.91699 7H11.0837" stroke="#006CDD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const getSampleValues = (item, appLinkOption, previewValuesFromApi = {}) => {
    const defaultAppLink = previewValuesFromApi?.app_link || 'https://apexautoapp.app.link/oHJ1lWxYqVb';

    return {
        customer_name: 'Jhon',
        shop_name: 'Vancouver Mall',
        shop_address: '8700 Northeast Vancouver Mall Drive, Vancouver, WA',
        appointment_day: 'Wednesday',
        appointment_date: 'Sep 24, 2026',
        appointment_time: '10:30 AM',
        reminder_time: '2 hours',
        old_appointment_day: 'Wednesday',
        old_appointment_date: 'Sep 17, 2026',
        old_appointment_time: '10:30 AM',
        new_appointment_day: 'Wednesday',
        new_appointment_date: 'Sep 24, 2026',
        new_appointment_time: '10:30 AM',
        vehicle: '2019 Honda Civic Touring',
        vehicle_description: '2019 Honda Civic Touring',
        propose_new_time_link: 'https://apex-customer.devstree.in/takaki-automotive/appointment/propose-new-time?appointmentId=0123456789abcdef01234567',
        web_booking_link: 'https://book.apexauto.app/abcdefghij',
        review_link: 'https://search.google.com/local/writereview?placeid=ChIJ0c6lfemvlVQRwAC16NXufZs',
        calendar_link: 'https://apex-api.devstree.in/c/K7mQx2Bv9pT',
        ...previewValuesFromApi,
        app_link: defaultAppLink,
        apex_auto_link: defaultAppLink,
    };
};

const renderPreviewWithLinks = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, index) => {
        if (urlRegex.test(part)) {
            return (
                <Typography
                    key={index}
                    component="a"
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        color: '#006CDD',
                        textDecoration: 'underline',
                        wordBreak: 'break-all',
                        fontSize: '14px',
                        lineHeight: '20px',
                    }}
                >
                    {part}
                </Typography>
            );
        }
        return part;
    });
};

const getTemplateHeader = (type) => {
    switch (type) {
        case 'appointmentReminder24h':
            return {
                title: 'Appointment Reminder',
                subtitle: '(24 hours before appointment)',
                Icon: AppointmentReminder24hIcon,
            };
        case 'appointmentReminder2h':
            return {
                title: 'Appointment Reminder',
                subtitle: '(2 hours before appointment)',
                Icon: AppointmentReminder2hIcon,
            };
        case 'appointmentConfirmed':
            return {
                title: 'Appointment Confirmed',
                subtitle: '',
                Icon: AppointmentConfirmedIcon,
            };
        case 'appointmentRescheduled':
            return {
                title: 'Appointment Rescheduled',
                subtitle: '',
                Icon: AppointmentRescheduledIcon,
            };
        case 'appointmentProposeNewTime':
            return {
                title: 'Propose New Time',
                subtitle: '',
                Icon: AfterHoursBookingIcon,
            };
        case 'afterHoursBookingRequest':
            return {
                title: 'After Hours Booking Request',
                subtitle: '',
                Icon: AfterHoursBookingIcon,
            };
        case 'vehicleReadyForPickup':
            return {
                title: 'Vehicle Ready for Pickup',
                subtitle: '',
                Icon: VehicleReadyIcon,
            };
        case 'reviewRequest':
            return {
                title: 'Review Request',
                subtitle: '',
                Icon: GoogleIcon,
            };
        default:
            return {
                title: type,
                subtitle: '',
                Icon: AppointmentConfirmedIcon,
            };
    }
};

const getSortIndex = (type) => {
    switch (type) {
        case 'appointmentReminder24h':
            return 1;
        case 'appointmentReminder2h':
            return 2;
        case 'appointmentConfirmed':
            return 3;
        case 'appointmentRescheduled':
        case 'appointmentReschedule':
            return 4;
        case 'afterHoursBookingRequest':
            return 5;
        case 'vehicleReadyForPickup':
            return 6;
        case 'appointmentProposeNewTime':
        case 'proposeNewTime':
            return 7;
        case 'reviewRequest':
            return 8;
        default:
            return 99;
    }
};

const AppointmentTextMessages = ({ configData: propConfigData }) => {
    const queryClient = useQueryClient();
    const { data: queryConfigData } = useGetShopConfig();
    const updateShopConfigMutation = useUpdateShopConfig();
    const shopConfig = propConfigData || queryConfigData;

    const { data: notificationTemplatesData, isLoading: isTemplatesLoading } = useGetCustomerNotificationTemplates();
    console.log('notificationTemplatesData', notificationTemplatesData);
    const updateNotificationTemplatesMutation = useUpdateCustomerNotificationTemplates();

    const { setSettingsDirty, resetSettingsDirty } = useGlobalStore();

    const editorRefs = useRef({});

    const [appLinkOption, setAppLinkOption] = useState('include');
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const [isResetAllModalOpen, setIsResetAllModalOpen] = useState(false);
    const [isAllCollapsed, setIsAllCollapsed] = useState(true);
    const [expandedItems, setExpandedItems] = useState({});
    const [messageTemplates, setMessageTemplates] = useState({});
    const [enabledStates, setEnabledStates] = useState({});

    const rawAppointmentList = notificationTemplatesData?.templates?.appointment_texts?.appointment || [];
    const appointmentList = [...rawAppointmentList].sort((a, b) => getSortIndex(a.type) - getSortIndex(b.type));

    // Hydrate appLinkOption from shop config API data
    useEffect(() => {
        const config = shopConfig?.config || shopConfig;
        if (config && config.includeApexAutoLinkInSms !== undefined) {
            setAppLinkOption(config.includeApexAutoLinkInSms ? 'include' : 'dont_include');
        }
    }, [shopConfig]);

    // Hydrate template state from customer notification templates API response
    useEffect(() => {
        if (notificationTemplatesData) {
            const list = notificationTemplatesData?.templates?.appointment_texts?.appointment || [];
            const msgMap = {};
            const enMap = {};
            list.forEach((item) => {
                msgMap[item.type] = item.messageTemplate || '';
                enMap[item.type] = item.isEnabled ?? true;
            });
            setExpandedItems((prev) => {
                const nextExpMap = { ...prev };
                list.forEach((item) => {
                    if (nextExpMap[item.type] === undefined) {
                        nextExpMap[item.type] = false;
                    }
                });
                return nextExpMap;
            });
            setMessageTemplates(msgMap);
            setEnabledStates(enMap);
        }
    }, [notificationTemplatesData]);

    // Derived: original values from API (stable references)
    const originalMsgMap = useMemo(() => {
        const map = {};
        const list = notificationTemplatesData?.templates?.appointment_texts?.appointment || [];
        list.forEach((item) => {
            map[item.type] = item.messageTemplate || '';
        });
        return map;
    }, [notificationTemplatesData]);

    const originalEnMap = useMemo(() => {
        const map = {};
        const list = notificationTemplatesData?.templates?.appointment_texts?.appointment || [];
        list.forEach((item) => {
            map[item.type] = item.isEnabled ?? true;
        });
        return map;
    }, [notificationTemplatesData]);

    const originalAppLinkOption = useMemo(() => {
        const config = shopConfig?.config || shopConfig;
        if (config && config.includeApexAutoLinkInSms !== undefined) {
            return config.includeApexAutoLinkInSms ? 'include' : 'dont_include';
        }
        return 'include';
    }, [shopConfig]);

    // Sync dirty state to global store whenever local state diverges from API data
    useEffect(() => {
        if (!notificationTemplatesData) return;
        const list = notificationTemplatesData?.templates?.appointment_texts?.appointment || [];
        const msgChanged = list.some((item) => messageTemplates[item.type] !== originalMsgMap[item.type]);
        const enChanged = list.some((item) => enabledStates[item.type] !== originalEnMap[item.type]);
        const linkChanged = appLinkOption !== originalAppLinkOption;
        setSettingsDirty(msgChanged || enChanged || linkChanged);
    }, [messageTemplates, enabledStates, appLinkOption, originalMsgMap, originalEnMap, originalAppLinkOption, notificationTemplatesData, setSettingsDirty]);

    // Clear dirty flag when component unmounts
    useEffect(() => {
        return () => resetSettingsDirty();
    }, [resetSettingsDirty]);

    const handleConfirmRemove = async () => {
        setIsRemoveModalOpen(false);
        setAppLinkOption('dont_include');
        try {
            const response = await updateShopConfigMutation.mutateAsync({
                includeApexAutoLinkInSms: false,
            });
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.customerNotificationTemplates() });
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.config() });
            if (response) {
                showTaxSettingsNotice(
                    'Apex Auto App links were removed, but references to Apex Auto remain. Please review each message before clicking save.',
                    8000
                );
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to update shop configuration');
            const config = shopConfig?.config || shopConfig;
            if (config && config.includeApexAutoLinkInSms !== undefined) {
                setAppLinkOption(config.includeApexAutoLinkInSms ? 'include' : 'dont_include');
            }
        }
    };

    const handleToggleChange = () => {
        if (appLinkOption === 'include') {
            setIsRemoveModalOpen(true);
        }
    };

    const toggleAllCollapse = () => {
        const nextState = !isAllCollapsed;
        setIsAllCollapsed(nextState);
        const updated = {};
        appointmentList.forEach((item) => {
            updated[item.type] = !nextState;
        });
        setExpandedItems(updated);
    };

    const toggleItemCollapse = (type) => {
        setExpandedItems((prev) => ({
            ...prev,
            [type]: !prev[type],
        }));
    };

    const handleEnableToggle = (type, val) => {
        if (val !== null) {
            const isEnable = val === 'enable';
            setEnabledStates((prev) => ({
                ...prev,
                [type]: isEnable,
            }));
            setExpandedItems((prev) => ({
                ...prev,
                [type]: isEnable,
            }));
        }
    };

    const handleMessageChange = (type, val) => {
        setMessageTemplates((prev) => ({
            ...prev,
            [type]: val,
        }));
    };

    const handleResetTemplate = (type, defaultMsg) => {
        setMessageTemplates((prev) => ({
            ...prev,
            [type]: defaultMsg,
        }));
        toast.success('Message reset to default');
    };

    const isAllTemplatesDefault = appointmentList.every((item) => {
        const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
        const isEnabled = enabledStates[item.type] !== false;
        const isDefaultMsg = currentMsg === item.defaultMessageTemplate;
        return isDefaultMsg && isEnabled;
    });

    const isResetAllDisabled = appLinkOption === 'include' && isAllTemplatesDefault;

    const handleResetAll = async () => {
        const updatedMsgMap = {};
        const updatedEnMap = {};

        appointmentList.forEach((item) => {
            updatedMsgMap[item.type] = item.defaultMessageTemplate;
            updatedEnMap[item.type] = true;
        });

        setMessageTemplates(updatedMsgMap);
        setEnabledStates(updatedEnMap);
        setAppLinkOption('include');

        try {
            await updateShopConfigMutation.mutateAsync({
                includeApexAutoLinkInSms: true,
            });

            const currentAppointmentArray = notificationTemplatesData?.templates?.appointment_texts?.appointment || [];

            const updatedAppointmentArray = currentAppointmentArray.map((item) => {
                const defaultMsg = item.defaultMessageTemplate;
                const matchedVars = defaultMsg.match(/\{([\w_]+)\}/g) || [];
                const uniqueVars = [];
                matchedVars.forEach((v) => {
                    const varName = v.replace(/[\{\}]/g, '');
                    if (!uniqueVars.includes(varName)) {
                        uniqueVars.push(varName);
                    }
                });

                const variablesPayload = uniqueVars.map((varName, idx) => ({
                    varName,
                    varOrder: idx + 1,
                    isRequired: item.requiredVariables?.includes(varName) || false,
                }));

                return {
                    ...item,
                    isEnabled: true,
                    messageTemplate: defaultMsg,
                    isCustomized: false,
                    variables: variablesPayload,
                };
            });

            const payload = {
                ...notificationTemplatesData,
                templates: {
                    ...notificationTemplatesData?.templates,
                    appointment_texts: {
                        ...notificationTemplatesData?.templates?.appointment_texts,
                        appointment: updatedAppointmentArray,
                    },
                },
            };

            const response = await updateNotificationTemplatesMutation.mutateAsync(payload);
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.customerNotificationTemplates() });
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.config() });
            toast.success(response?.message || 'All templates reset to default');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to reset templates');
        }
    };

    const handleConfirmResetAll = async () => {
        setIsResetAllModalOpen(false);
        await handleResetAll();
    };

    // Inserting variable chip at the exact caret/cursor position inside the editor
    const handleVariableChipClick = (type, varName) => {
        const editor = editorRefs.current[type];
        if (editor?.insertVariable) {
            editor.insertVariable(varName);
        } else {
            const currentMsg = messageTemplates[type] || '';
            const varToken = `{${varName}}`;
            setMessageTemplates((prev) => ({
                ...prev,
                [type]: currentMsg ? `${currentMsg} ${varToken}` : varToken,
            }));
        }
    };

    const generatePreviewText = (templateStr, item) => {
        if (templateStr === item?.messageTemplate && item?.preview?.text) {
            let previewText = item.preview.text;
            if (appLinkOption === 'dont_include') {
                previewText = previewText
                    .replace(/https?:\/\/apexautoapp\.app\.link\/[^\s]+/g, '')
                    .replace(/:\s*$/, '')
                    .trim();
            }
            return previewText;
        }

        let result = templateStr || '';
        const samples = getSampleValues(item, appLinkOption, notificationTemplatesData?.previewValues);

        Object.keys(samples).forEach((key) => {
            const regex = new RegExp(`\\{${key}\\}`, 'g');
            result = result.replace(regex, samples[key]);
        });

        if (item?.appLinkText && appLinkOption === 'include') {
            let appLinkFormatted = item.appLinkText;
            Object.keys(samples).forEach((key) => {
                const regex = new RegExp(`\\{${key}\\}`, 'g');
                appLinkFormatted = appLinkFormatted.replace(regex, samples[key]);
            });
            result += ` ${appLinkFormatted}`;
        }
        return result.trim();
    };

    const doCancel = () => {
        if (notificationTemplatesData) {
            const list = notificationTemplatesData?.templates?.appointment_texts?.appointment || [];
            const msgMap = {};
            const enMap = {};
            list.forEach((item) => {
                msgMap[item.type] = item.messageTemplate || '';
                enMap[item.type] = item.isEnabled ?? true;
            });
            setMessageTemplates(msgMap);
            setEnabledStates(enMap);
        }
        resetSettingsDirty();
    };

    const handleCancel = () => {
        doCancel();
    };

    const handleSave = async () => {
        const itemsWithMissingVars = appointmentList.filter((item) => {
            const isEnabled = enabledStates[item.type] !== false;
            const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
            return isEnabled && getMissingRequiredVariables(item, currentMsg).length > 0;
        });

        const itemsWithExceededLimit = appointmentList.filter((item) => {
            const isEnabled = enabledStates[item.type] !== false;
            const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
            const previewText = generatePreviewText(currentMsg, item);
            return isEnabled && previewText && getGSM7Length(previewText) > 280;
        });

        const itemsWithInvalidGSM7 = appointmentList.filter((item) => {
            const isEnabled = enabledStates[item.type] !== false;
            const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
            return isEnabled && hasInvalidGSM7Characters(currentMsg);
        });

        const allInvalidItems = [...itemsWithMissingVars, ...itemsWithExceededLimit, ...itemsWithInvalidGSM7].filter(
            (item, index, self) => self.findIndex((t) => t.type === item.type) === index
        );

        if (allInvalidItems.length > 0) {
            setExpandedItems((prev) => {
                const next = { ...prev };
                allInvalidItems.forEach((item) => {
                    next[item.type] = true;
                });
                return next;
            });

            if (itemsWithMissingVars.length > 0) {
                const totalMissingVars = itemsWithMissingVars.reduce((sum, item) => {
                    const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
                    return sum + getMissingRequiredVariables(item, currentMsg).length;
                }, 0);

                if (itemsWithMissingVars.length === 1 && totalMissingVars === 1) {
                    const item = itemsWithMissingVars[0];
                    const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
                    const missingVars = getMissingRequiredVariables(item, currentMsg);
                    const cleanVar = missingVars[0]
                        .replace(/[\{\}]/g, '')
                        .replace(/_/g, ' ')
                        .trim();
                    const varName = cleanVar ? cleanVar.charAt(0).toUpperCase() + cleanVar.slice(1) : missingVars[0];
                    const headerInfo = getTemplateHeader(item.type);
                    const title = item.title || headerInfo?.title || item.type;
                    toast.error(`“${varName}” is missing from “${title}.” Add it before saving`);
                } else {
                    toast.error('Some messages are missing required details. Review the highlighted messages before saving');
                }
            } else if (itemsWithExceededLimit.length > 0) {
                toast.error('Some messages exceed the 280-character limit. Shorten them before saving');
            } else if (itemsWithInvalidGSM7.length > 0) {
                toast.error('Message contains invalid characters. Please remove and try again.');
            }
            return;
        }

        try {
            const currentAppointmentArray = notificationTemplatesData?.templates?.appointment_texts?.appointment || [];

            const updatedAppointmentArray = currentAppointmentArray.map((item) => {
                const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
                const isEnabled = enabledStates[item.type] ?? item.isEnabled;

                // Extract variables in order of appearance in currentMsg
                const matchedVars = currentMsg.match(/\{([\w_]+)\}/g) || [];
                const uniqueVars = [];
                matchedVars.forEach((v) => {
                    const varName = v.replace(/[\{\}]/g, '');
                    if (!uniqueVars.includes(varName)) {
                        uniqueVars.push(varName);
                    }
                });

                const variablesPayload = uniqueVars.map((varName, idx) => ({
                    varName,
                    varOrder: idx + 1,
                    isRequired: item.requiredVariables?.includes(varName) || false,
                }));

                return {
                    ...item,
                    isEnabled,
                    messageTemplate: currentMsg,
                    isCustomized: currentMsg !== item.defaultMessageTemplate,
                    variables: variablesPayload,
                };
            });

            const payload = {
                ...notificationTemplatesData,
                templates: {
                    ...notificationTemplatesData?.templates,
                    appointment_texts: {
                        ...notificationTemplatesData?.templates?.appointment_texts,
                        appointment: updatedAppointmentArray,
                    },
                },
            };

            const response = await updateNotificationTemplatesMutation.mutateAsync(payload);
            resetSettingsDirty();
            toast.success(response?.message || 'Changes Saved');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to save changes');
        }
    };

    return (
        <Box>
            <Grid
                container
                sx={{
                    boxShadow: 3,
                    bgcolor: 'white',
                    padding: '24px',
                    borderRadius: '10px',
                    marginTop: '32px',
                    justifyContent: 'space-between',
                }}
            >
                {/* Left Column: Title, Description, Divider, Description 2 */}
                <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
                    <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px', color: '#1A1A1A' }}>
                        Appointment Text Messages
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            mt: '12px',
                            fontSize: '16px',
                            color: '#000000',
                            lineHeight: '24px',
                            letterSpacing: '0.15px',
                            maxWidth: 360,
                        }}
                    >
                        Customize SMS messages sent to customers.
                    </Typography>

                    <Divider sx={{ my: '12px', borderColor: 'rgba(0, 0, 0, 0.10)', maxWidth: 360 }} />

                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '16px',
                            color: '#000000',
                            lineHeight: '24px',
                            letterSpacing: '0.15px',
                            maxWidth: 360,
                        }}
                    >
                        Click a variable to insert it — it will be replaced with real details when the message is sent.
                    </Typography>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} sm={8} md={8} lg={8} xl={8}>
                    {/* Top Row: Include Link Setting */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '32px' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: '20px',
                                    fontWeight: 500,
                                    lineHeight: '160%',
                                    letterSpacing: '0.15px',
                                    color: '#1A1A1A',
                                }}
                            >
                                Apex Auto App Links
                            </Typography>
                            <Typography
                                sx={{
                                    maxWidth: 530,
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    lineHeight: '150%',
                                    letterSpacing: '0.15px',
                                    mt: '4px',
                                    color: '#4B5563',
                                }}
                            >
                                {appLinkOption === 'include'
                                    ? 'Apex Auto App download links are included in each appointment message. Clicking remove will remove all links from all messages.'
                                    : 'Apex Auto links have been removed. To restore them, reset messages to default.'}
                            </Typography>
                        </Box>

                        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                            <Box
                                onClick={() => {
                                    if (appLinkOption === 'include') {
                                        handleToggleChange(null, 'dont_include');
                                    }
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    borderRadius: '4px',
                                    bgcolor: 'rgba(211, 47, 47, 0.10)',
                                    color: '#D32F2F',
                                    px: '22px',
                                    py: '8px',
                                    height: '42px',
                                    boxSizing: 'border-box',
                                    cursor: appLinkOption === 'include' ? 'pointer' : 'default',
                                    fontSize: '15px',
                                    fontWeight: 500,
                                    lineHeight: '26px',
                                    letterSpacing: '0.46px',
                                    textTransform: 'uppercase',
                                    opacity: appLinkOption === 'include' ? 1 : 0.5,
                                    userSelect: 'none',
                                    pointerEvents: appLinkOption === 'include' ? 'auto' : 'none',
                                    '&:hover': {
                                        opacity: appLinkOption === 'include' ? 0.85 : 0.5,
                                    },
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 18, color: '#D32F2F' }} />
                                <span>{appLinkOption === 'include' ? 'REMOVE' : 'REMOVED'}</span>
                            </Box>
                        </Box>
                    </Box>

                    {/* Divider line */}
                    <Divider sx={{ my: '24px', borderColor: 'rgba(0, 0, 0, 0.10)' }} />

                    {/* Main Collapse All Toggle & Reset All Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '16px' }}>
                        <Box
                            onClick={() => {
                                if (!isResetAllDisabled) {
                                    setIsResetAllModalOpen(true);
                                }
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#D32F2F',
                                cursor: isResetAllDisabled ? 'default' : 'pointer',
                                opacity: isResetAllDisabled ? 0.4 : 1,
                                fontSize: '15px',
                                fontWeight: 500,
                                lineHeight: '130%',
                                letterSpacing: '0.46px',
                                textTransform: 'uppercase',
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path
                                    d="M12.5 7.5H17.5V2.5M17.0732 12.5C16.0437 15.413 13.2656 17.5 10 17.5C5.85786 17.5 2.5 14.1422 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C13.1077 2.5 15.7738 4.39005 16.9117 7.08333"
                                    stroke="#D32F2F"
                                    strokeWidth="1.5"
                                    strokeLinecap="square"
                                />
                            </svg>

                            <span>RESET ALL TO DEFAULT</span>
                        </Box>

                        <Box
                            onClick={toggleAllCollapse}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                color: '#006CDD',
                                cursor: 'pointer',
                                userSelect: 'none',
                                fontFeatureSettings: "'liga' off, 'clig' off",
                                fontFamily: 'Roboto',
                                fontSize: '15px',
                                fontStyle: 'normal',
                                fontWeight: 500,
                                lineHeight: '26px',
                                letterSpacing: '0.46px',
                                textTransform: 'uppercase',
                                '&:hover': { opacity: 0.85 },
                            }}
                        >
                            {isAllCollapsed ? <DoubleDownArrowIcon /> : <DoubleUpArrowIcon />}
                            <span>{isAllCollapsed ? 'EXPAND ALL' : 'COLLAPSE ALL'}</span>
                        </Box>
                    </Box>

                    {/* List of 8 Appointment Text Templates */}
                    <Stack spacing={2}>
                        {appointmentList.map((item) => {
                            const headerInfo = getTemplateHeader(item.type);
                            const IconComponent = headerInfo.Icon;
                            const isItemExpanded = expandedItems[item.type] === true;
                            const isEnabled = enabledStates[item.type] !== false;
                            const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
                            const previewText = generatePreviewText(currentMsg, item);

                            // Filter allowedVariables: only show variables NOT currently present in currentMsg
                            const unaddedVariables = (item.allowedVariables || []).filter((v) => !currentMsg.toLowerCase().includes(`{${v.toLowerCase()}}`));

                            const requiredVarNames = (
                                item.requiredVariables ||
                                (item.variables || []).filter((v) => v.isRequired).map((v) => v.varName) ||
                                []
                            ).map((v) => v.replace(/[{}]/g, '').toLowerCase());

                            const unaddedRequired = unaddedVariables.filter((v) => requiredVarNames.includes(v.toLowerCase()));
                            const unaddedOptional = unaddedVariables.filter((v) => !requiredVarNames.includes(v.toLowerCase()));

                            const missingReqVars = getMissingRequiredVariables(item, currentMsg);
                            const hasMissingReqVars = missingReqVars.length > 0;
                            const hasInvalidChars = hasInvalidGSM7Characters(currentMsg);
                            const previewLength = getGSM7Length(previewText);
                            const isCharOverflow = previewLength > 280;
                            const isResetDisabled = currentMsg === item.defaultMessageTemplate;

                            return (
                                <Box
                                    key={item.type}
                                    sx={{
                                        borderRadius: '8px',
                                        border: '1px solid rgba(0, 0, 0, 0.10)',
                                        bgcolor: '#FFF',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {/* Template Card Header */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            bgcolor: '#F8F8F8',
                                            p: '16px',
                                        }}
                                        onClick={() => toggleItemCollapse(item.type)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <IconComponent style={{ width: 20, height: 20 }} />
                                            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1A1A1A' }}>
                                                {headerInfo.title}{' '}
                                                {headerInfo.subtitle && (
                                                    <Typography component="span" sx={{ fontWeight: 400, color: '#666666' }}>
                                                        {headerInfo.subtitle}
                                                    </Typography>
                                                )}
                                            </Typography>
                                        </Box>

                                        <Stack direction="row" spacing={1.5} alignItems="center" onClick={(e) => e.stopPropagation()}>
                                            <StyledSmallToggleButtonGroup
                                                size="small"
                                                value={isEnabled ? 'enable' : 'disable'}
                                                exclusive
                                                onChange={(e, val) => handleEnableToggle(item.type, val)}
                                            >
                                                <ToggleButton value="enable">{isEnabled ? 'Enabled' : 'Enable'}</ToggleButton>
                                                <ToggleButton value="disable">{isEnabled ? 'Disable' : 'Disabled'}</ToggleButton>
                                            </StyledSmallToggleButtonGroup>

                                            <IconButton size="small" onClick={() => toggleItemCollapse(item.type)}>
                                                {isItemExpanded ? (
                                                    <KeyboardArrowUpIcon sx={{ color: '#666' }} />
                                                ) : (
                                                    <KeyboardArrowDownIcon sx={{ color: '#666' }} />
                                                )}
                                            </IconButton>
                                        </Stack>
                                    </Box>

                                    {/* Template Card Collapsible Body */}
                                    <Collapse in={isItemExpanded}>
                                        <Box
                                            sx={{
                                                p: '16px',
                                                bgcolor: '#FFF',
                                                opacity: isEnabled ? 1 : 0.4,
                                                pointerEvents: isEnabled ? 'auto' : 'none',
                                                userSelect: isEnabled ? 'auto' : 'none',
                                            }}
                                        >
                                            {/* Allowed Variables Section (Required & Optional Rows) */}
                                            {unaddedVariables.length > 0 && (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', mb: '16px' }}>
                                                    {/* Required Variables Row */}
                                                    {unaddedRequired.length > 0 && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                            <Typography
                                                                sx={{
                                                                    color: '#757575',
                                                                    fontSize: '14px',
                                                                    fontWeight: 400,
                                                                    lineHeight: '130%',
                                                                    minWidth: '64px',
                                                                }}
                                                            >
                                                                Required
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                                                                {unaddedRequired.map((v) => (
                                                                    <Box
                                                                        key={v}
                                                                        onClick={() => handleVariableChipClick(item.type, v)}
                                                                        sx={{
                                                                            borderRadius: '6px',
                                                                            bgcolor: 'rgba(0, 108, 221, 0.08)',
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            padding: '4px 6px',
                                                                            gap: '4px',
                                                                            cursor: 'pointer',
                                                                            userSelect: 'none',
                                                                            '&:hover': {
                                                                                bgcolor: 'rgba(0, 108, 221, 0.14)',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <PlusIcon />
                                                                        <Typography
                                                                            sx={{
                                                                                color: '#006CDD',
                                                                                fontSize: '12px',
                                                                                fontWeight: 400,
                                                                                lineHeight: '130%',
                                                                            }}
                                                                        >
                                                                            {v}
                                                                        </Typography>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}

                                                    {/* Optional Variables Row */}
                                                    {unaddedOptional.length > 0 && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                            <Typography
                                                                sx={{
                                                                    color: '#757575',
                                                                    fontSize: '14px',
                                                                    fontWeight: 400,
                                                                    lineHeight: '130%',
                                                                    minWidth: '64px',
                                                                }}
                                                            >
                                                                Optional
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                                                                {unaddedOptional.map((v) => (
                                                                    <Box
                                                                        key={v}
                                                                        onClick={() => handleVariableChipClick(item.type, v)}
                                                                        sx={{
                                                                            borderRadius: '6px',
                                                                            bgcolor: 'rgba(0, 108, 221, 0.08)',
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            padding: '4px 6px',
                                                                            gap: '4px',
                                                                            cursor: 'pointer',
                                                                            userSelect: 'none',
                                                                            '&:hover': {
                                                                                bgcolor: 'rgba(0, 108, 221, 0.14)',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <PlusIcon />
                                                                        <Typography
                                                                            sx={{
                                                                                color: '#006CDD',
                                                                                fontSize: '12px',
                                                                                fontWeight: 400,
                                                                                lineHeight: '130%',
                                                                            }}
                                                                        >
                                                                            {v}
                                                                        </Typography>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}

                                            {/* Message and Preview Side-by-Side */}
                                            <Grid container spacing={2.5}>
                                                {/* Left: Message Editor Box */}
                                                <Grid item xs={12} md={6}>
                                                    <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: '8px', color: '#1A1A1A' }}>Message</Typography>
                                                    <Box
                                                        sx={{
                                                            borderRadius: '8px',
                                                            border:
                                                                hasInvalidChars || hasMissingReqVars ? '1px solid #D32F2F' : '1px solid rgba(0, 0, 0, 0.10)',
                                                            bgcolor: '#FFF',
                                                            boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.06)',
                                                            p: '12px 16px',
                                                            height: '320px',
                                                            overflowY: 'auto',
                                                        }}
                                                    >
                                                        <MessageTemplateEditor
                                                            ref={(el) => {
                                                                editorRefs.current[item.type] = el;
                                                            }}
                                                            value={currentMsg}
                                                            onChange={(val) => handleMessageChange(item.type, val)}
                                                            placeholder="Edit message..."
                                                        />
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: '8px' }}>
                                                        <Typography
                                                            component="span"
                                                            onClick={() => {
                                                                if (!isResetDisabled) {
                                                                    handleResetTemplate(item.type, item.defaultMessageTemplate);
                                                                }
                                                            }}
                                                            sx={{
                                                                color: '#D32F2F',
                                                                fontSize: '15px',
                                                                fontWeight: 500,
                                                                lineHeight: '130%',
                                                                letterSpacing: '0.46px',
                                                                textTransform: 'uppercase',
                                                                cursor: isResetDisabled ? 'default' : 'pointer',
                                                                opacity: isResetDisabled ? 0.4 : 1,
                                                            }}
                                                        >
                                                            RESET TO DEFAULT
                                                        </Typography>
                                                    </Box>
                                                    {hasInvalidChars && (
                                                        <Typography
                                                            sx={{
                                                                color: '#D32F2F',
                                                                fontSize: '14px',
                                                                fontWeight: 400,
                                                                lineHeight: '130%',
                                                                mt: '12px',
                                                            }}
                                                        >
                                                            Message contains invalid characters. Please remove and try again.
                                                        </Typography>
                                                    )}
                                                    {!hasInvalidChars && hasMissingReqVars && (
                                                        <Typography
                                                            sx={{
                                                                color: '#D32F2F',
                                                                fontSize: '14px',
                                                                fontWeight: 400,
                                                                lineHeight: '130%',
                                                                mt: '12px',
                                                            }}
                                                        >
                                                            {getMissingVariablesErrorMessage(missingReqVars)}
                                                        </Typography>
                                                    )}
                                                </Grid>

                                                {/* Right: Preview Box */}
                                                <Grid item xs={12} md={6}>
                                                    <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: '8px', color: '#1A1A1A' }}>Preview</Typography>
                                                    <Box
                                                        sx={{
                                                            borderRadius: '8px',
                                                            border: isCharOverflow ? '1px solid #D32F2F' : '1px solid rgba(0, 0, 0, 0.10)',
                                                            bgcolor: '#FFF',
                                                            boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.06)',
                                                            p: '12px 16px',
                                                            height: '320px',
                                                            overflowY: 'auto',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                borderRadius: '12px',
                                                                bgcolor: '#F0F0F0',
                                                                maxWidth: '260px',
                                                                p: '14px 12px',
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '14px',
                                                                    color: '#374151',
                                                                    lineHeight: '20px',
                                                                    whiteSpace: 'pre-line',
                                                                    wordBreak: 'break-word',
                                                                }}
                                                            >
                                                                {renderPreviewWithLinks(previewText)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mt: '8px' }}>
                                                        <Typography
                                                            sx={{ fontSize: '14px', lineHeight: '130%', color: isCharOverflow ? '#D32F2F' : '#9E9E9E' }}
                                                        >
                                                            {previewLength} / 280 characters
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </Stack>

                    {/* Bottom Right Actions */}
                    <Grid container justifyContent="flex-end" spacing={2} sx={{ marginTop: '24px' }}>
                        <Grid item>
                            <Stack spacing={2} direction="row">
                                <Button variant="outlined" color="primary" onClick={handleCancel}>
                                    CANCEL
                                </Button>
                                <Button variant="contained" color="primary" onClick={handleSave} disabled={updateNotificationTemplatesMutation.isPending}>
                                    SAVE
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Confirmation Modal for Removing Apex Auto Links */}
            <CommonDialog
                open={isRemoveModalOpen}
                onClose={() => setIsRemoveModalOpen(false)}
                onSave={handleConfirmRemove}
                loader={updateShopConfigMutation.isPending}
                dialogTitle="⚠️ Remove Apex Auto links?"
                dialogSubtitle="Are you sure you want to remove Apex Auto App download link from all appointment messages?"
                dialogSubtitle2="The content of the messages will not change, so review each message before saving."
                saveButtonText="REMOVE"
                saveButtonType="error"
                cancelButtonText="CANCEL"
            />

            {/* Confirmation Modal for Resetting All Messages to Default */}
            <CommonDialog
                open={isResetAllModalOpen}
                onClose={() => setIsResetAllModalOpen(false)}
                onSave={handleConfirmResetAll}
                loader={updateNotificationTemplatesMutation.isPending || updateShopConfigMutation.isPending}
                dialogTitle="⚠️ Reset all messages to default?"
                dialogSubtitle="Resetting will replace all customized appointment text messages with the default templates."
                dialogSubtitle2="Your changes will be lost."
                saveButtonText="RESET ALL"
                saveButtonType="error"
                saveButtonWidth="126px"
                cancelButtonText="CANCEL"
            />

            {/* Dialog Loader during initial loading or updating */}
            <Dialog fullScreen={false} open={isTemplatesLoading || updateNotificationTemplatesMutation.isPending || updateShopConfigMutation.isPending}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default AppointmentTextMessages;
